import ActionButton from "@/components/buttons/ActionButton";
import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import AntIcon from "@/components/icons/AntIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { formatPhoneNumber } from "@/utils/formatUtils";
import { Button, Col, Collapse, Divider, Popconfirm, Row, Space, Tag, Tree } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  useAdminDeleteAgentUserAccount,
  useAdminResendUserConfirmLink,
  useEnumeration,
  useGetPermissionDefinitions,
} from "../../../../../common/api/queries";
import { formatAgentName } from "../../../../agent/utils";
import { useIsSystemAdmin } from "../../../../auth/hooks";
import { userAccountRemoved } from "../../../../auth/session";
import { EnumerationType } from "../../../../enumerations/enums";
import { ADMIN_USER_ROUTE_PATHS } from "../../../paths";
import { AgentUserAccount, UserAdminView } from "../../../types";
import {
  buildPermissionTreeNodesView,
  hasConfirmedAgentUserAccount,
  hasDisabledAgentUserAccountsOnly,
} from "../../../utils";

interface Props {
  user: UserAdminView;
  onUpdateUserClick: () => void;
  onUpdateUserAccountClick: (account: AgentUserAccount) => void;
}

export const AdminUserDetailView = ({ user, onUpdateUserClick, onUpdateUserAccountClick }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const isCurrentUserSystemAdmin = useIsSystemAdmin();
  const topAgentAllowedFeatures = useEnumeration(EnumerationType.TOP_AGENT_ALLOWED_FEATURES);

  const { data: permissionDefinitions } = useGetPermissionDefinitions();

  const adminDeleteAgentUserAccount = useAdminDeleteAgentUserAccount();
  const adminResendUserConfirmLink = useAdminResendUserConfirmLink();

  return (
    <>
      <Divider titlePlacement="start" style={{ marginTop: 0 }}>
        {t("user.titles.basicData")}
      </Divider>

      <ItemCreatedUpdatedInfoView item={user} className="margin-bottom-medium" />

      <table className="data-table-view margin-top-medium margin-bottom-small">
        <tbody>
          <tr>
            <td>{t("user.attrs.name")}:</td>
            <td>{user.name}</td>
            <td>
              {t("user.attrs.wrongLoginAttempts")}: {user.wrongLoginAttempts}
            </td>
          </tr>
          <tr>
            <td>{t("user.attrs.email")}:</td>
            <td>{user.email || user.emailToConfirm}</td>
            <td>
              <Space>
                {user.locked && <Tag color="red">{t("user.helpers.lockedUser")}</Tag>}

                {hasDisabledAgentUserAccountsOnly(user) && <Tag color="purple">{t("user.helpers.disabledUser")}</Tag>}

                {!hasConfirmedAgentUserAccount(user) && (
                  <>
                    <Tag color="orange">{t("user.helpers.unconfirmedUser")}</Tag>
                    <Popconfirm
                      title={t("user.titles.resendConfirmLinkConfirm")}
                      icon={<PopconfirmDeleteIcon />}
                      okText={t("common.yes")}
                      cancelText={t("common.no")}
                      okType="danger"
                      onConfirm={() => adminResendUserConfirmLink.mutate({ id: user.id })}
                    >
                      <SecondaryButton size="small" type="primary" icon={<AntIcon type="mail" />}>
                        {t("user.actions.resendConfirmLink")}
                      </SecondaryButton>
                    </Popconfirm>
                  </>
                )}

                {user.agentUserRole?.systemAdmin && <Tag color="cyan">{t("user.attrs.systemAdmin")}</Tag>}
              </Space>
            </td>
          </tr>
          <tr>
            <td>{t("user.attrs.phone")}:</td>
            <td>
              {formatPhoneNumber(hasConfirmedAgentUserAccount(user) ? (user.phone ?? "") : (user.phoneToConfirm ?? ""))}
            </td>
          </tr>
        </tbody>
      </table>

      <Button
        className="margin-bottom-tiny"
        size="small"
        type="primary"
        icon={<AntIcon type="edit" />}
        onClick={onUpdateUserClick}
      >
        {t("user.actions.updateBasicUserData")}
      </Button>

      <Divider titlePlacement="start">{t("user.titles.accounts")}</Divider>

      <Collapse
        accordion
        className="margin-top-medium"
        items={user.agentUserRole?.userAccounts.map((account) => ({
          key: account.id,
          label: account.representingAgent
            ? `${formatAgentName(account.agent)} | ${formatAgentName(account.representingAgent)}`
            : formatAgentName(account.agent),
          extra: (
            <>
              {account.disabled && <Tag color="purple">{t("user.helpers.disabledAccount")}</Tag>}
              <span onClick={(event) => event.stopPropagation()}>
                <Button
                  className="margin-right-tiny"
                  type="primary"
                  size="small"
                  icon={<AntIcon type="edit" />}
                  onClick={() => onUpdateUserAccountClick(account)}
                >
                  {t("user.actions.updateAccount")}
                </Button>
                <Popconfirm
                  title={
                    <div style={{ maxWidth: 450 }}>
                      <Row>
                        <Col>
                          {t("user.titles.deleteAgentAccountConfirm", {
                            aggregatedName: account.agent?.aggregatedName,
                          })}
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          {user.agentUserRole?.userAccounts.length === 1
                            ? t("user.helpers.deleteLastAccountInfo")
                            : t("user.helpers.updateInfo")}
                        </Col>
                      </Row>
                    </div>
                  }
                  icon={<PopconfirmDeleteIcon />}
                  okText={t("common.yes")}
                  cancelText={t("common.no")}
                  okType="danger"
                  onConfirm={() =>
                    adminDeleteAgentUserAccount.mutate(
                      { id1: user.id, id2: account.id },
                      {
                        onSuccess: () => {
                          userAccountRemoved(user.id, account.id);

                          if (user.agentUserRole?.userAccounts.length === 1) {
                            navigate(ADMIN_USER_ROUTE_PATHS.list.to, { replace: true });
                          }
                        },
                      },
                    )
                  }
                >
                  <ActionButton icon="minus">{t("user.actions.deleteAccount")}</ActionButton>
                </Popconfirm>
              </span>
            </>
          ),
          children: (
            <>
              <ItemCreatedUpdatedInfoView item={account} className="margin-bottom-medium" />

              <h4>{t("user.attrs.permissionsLabel")}</h4>
              <Tree
                showLine={{ showLeafIcon: false }}
                treeData={buildPermissionTreeNodesView(
                  permissionDefinitions,
                  topAgentAllowedFeatures,
                  account.permissions,
                  isCurrentUserSystemAdmin,
                )}
              />
            </>
          ),
        }))}
      />
    </>
  );
};
